
import { useState, useRef, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAdelantoCart } from '@/contexts/AdelantoCartContext';
import { adelantosService, VentaListItem, VentaCuotasResponse } from '@/lib/adelantos-service';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { ADQUIRENTE_STYLES } from './types';

const fmt = (n: number | null) =>
  n === null ? '---' : `$${Math.abs(n).toLocaleString('es-CL')}`;
const fmtNeg = (n: number | null) =>
  n === null ? '---' : `-$${Math.abs(n).toLocaleString('es-CL')}`;

function ComisionCell({ comision, tye, size = 'sm' }: { comision: number | null; tye: number | null; size?: 'sm' | 'xs' }) {
  if (comision === null) return <span>---</span>;

  const total = comision + (tye ?? 0);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-default border-b border-dashed border-slate-300 dark:border-slate-600">
          <span>{fmtNeg(comision)}</span>
          {tye !== null && tye !== 0 && (
            <span className={`ml-1 text-slate-400 ${size === 'xs' ? 'text-[10px]' : 'text-xs'}`}>
              ({fmtNeg(tye)} TyE)
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="p-3 max-w-xs">
        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between gap-6">
            <span className="text-slate-500">Comision FLUX</span>
            <span className="font-medium">{fmtNeg(comision)}</span>
          </div>
          {tye !== null && tye !== 0 && (
            <div className="flex justify-between gap-6">
              <span className="text-slate-500">Timbre y Estampillas (TyE)</span>
              <span className="font-medium">{fmtNeg(tye)}</span>
            </div>
          )}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-1.5 flex justify-between gap-6">
            <span className="font-semibold">Total Descuento</span>
            <span className="font-bold">{fmtNeg(total)}</span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function AdquirenteBadge({ name }: { name: string }) {
  const s = ADQUIRENTE_STYLES[name] ?? {
    code: name.slice(0, 2),
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-600',
  };
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-6 h-6 rounded ${s.bgColor} flex items-center justify-center text-[10px] font-bold ${s.textColor}`}
      >
        {s.code}
      </div>
      <span className="text-sm font-semibold">{name}</span>
    </div>
  );
}

function ParentCheckbox({
  ventaId,
  onToggle,
}: {
  ventaId: string;
  onToggle: () => void;
}) {
  const { isAllSelected, isIndeterminate } = useAdelantoCart();
  const ref = useRef<HTMLInputElement>(null);
  const indeterminate = isIndeterminate(ventaId);
  const checked = isAllSelected(ventaId);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      className="h-4.5 w-4.5 cursor-pointer rounded border-slate-300 accent-flux-primary focus:ring-2 focus:ring-flux-primary dark:border-slate-600"
    />
  );
}

interface VentaRowProps {
  venta: VentaListItem;
  expanded: boolean;
  onToggleExpand: () => void;
  onLoadCuotas: (ventaId: string) => Promise<VentaCuotasResponse | null>;
  cuotasData: VentaCuotasResponse | null;
  loadingCuotas: boolean;
}

function VentaRow({ venta, expanded, onToggleExpand, onLoadCuotas, cuotasData, loadingCuotas }: VentaRowProps) {
  const { isCuotaSelected, toggleCuota, toggleAllCuotas, selections, ventaRegistry } = useAdelantoCart();

  const handleParentToggle = useCallback(async () => {
    const registeredVenta = ventaRegistry[venta.id];
    if (!registeredVenta || registeredVenta.cuotas.length === 0) {
      const freshData = await onLoadCuotas(venta.id);
      if (freshData) {
        toggleAllCuotas(venta.id, freshData.cuotas.map((c) => c.id));
        return;
      }
    }
    toggleAllCuotas(venta.id);
  }, [ventaRegistry, venta.id, onLoadCuotas, toggleAllCuotas]);

  const selectedCuotaIds = selections[venta.id] ?? [];
  const registeredVenta = ventaRegistry[venta.id];
  const selectedCuotas = registeredVenta
    ? registeredVenta.cuotas.filter((c) => selectedCuotaIds.includes(c.id))
    : [];
  const hasSelection = selectedCuotas.length > 0;
  const allSelected = registeredVenta && registeredVenta.cuotas.length > 0
    && selectedCuotas.length === registeredVenta.cuotas.length;
  const sumComision = hasSelection
    ? allSelected && registeredVenta.comisionFlux !== null
      ? registeredVenta.comisionFlux
      : selectedCuotas.reduce((sum, c) => sum + c.comisionFlux, 0)
    : null;
  const sumTye = hasSelection && registeredVenta?.tye !== null
    ? registeredVenta.tye
    : null;
  const sumRecibiras = hasSelection
    ? allSelected && registeredVenta.recibiras !== null
      ? registeredVenta.recibiras
      : selectedCuotas.reduce((sum, c) => sum + c.recibiras, 0)
    : null;

  return (
    <>
      <tr
        className={`hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors ${
          expanded ? 'bg-slate-50/80 dark:bg-slate-800/10' : ''
        }`}
      >
        <td className="px-4 py-4">
          <ParentCheckbox ventaId={venta.id} onToggle={handleParentToggle} />
        </td>
        <td className="px-4 py-4">
          <button
            onClick={onToggleExpand}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">
              {expanded ? 'remove_circle' : 'add_circle'}
            </span>
          </button>
        </td>
        <td className="px-4 py-4 text-sm font-medium text-slate-700 dark:text-slate-300">
          {venta.fechaVenta}
        </td>
        <td className="px-4 py-4">
          <AdquirenteBadge name={venta.adquirente} />
        </td>
        <td className="px-4 py-4 text-sm font-mono text-slate-600 dark:text-slate-400">
          {venta.commerceCode ?? '---'}
        </td>
        <td className="px-4 py-4 text-sm font-mono text-slate-600 dark:text-slate-400">
          {venta.authorizationCode ?? '---'}
        </td>
        <td className="px-4 py-4 text-sm">
          {(() => {
            const count = Number(venta.cuotaDisplay.split(' de ')[0]) || 0;
            return `${count} ${count === 1 ? 'disponible' : 'disponibles'}`;
          })()}
        </td>
        <td className="px-4 py-4 text-sm font-semibold">{fmt(venta.abonoOriginal)}</td>
        <td className="px-4 py-4 text-sm text-slate-500">
          <ComisionCell comision={sumComision} tye={sumTye} />
        </td>
        <td className="px-4 py-4 text-sm font-bold text-emerald-600 dark:text-emerald-400">
          {fmt(sumRecibiras)}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-primary/5 dark:bg-primary/5">
          <td colSpan={10} className="px-8 py-0">
            <div className="border-l-2 border-primary/30 ml-10 my-4 pl-6 overflow-hidden">
              {loadingCuotas ? (
                <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                  <span className="material-symbols-outlined animate-spin text-base">
                    progress_activity
                  </span>
                  Cargando cuotas...
                </div>
              ) : cuotasData && cuotasData.cuotas.length > 0 ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400 font-semibold uppercase">
                      <th className="pb-2 w-8" />
                      <th className="pb-2">Detalle de la cuota</th>
                      <th className="pb-2">Abono original</th>
                      <th className="pb-2">Monto Original</th>
                      <th className="pb-2">Días adelanto</th>
                      <th className="pb-2">Comisión FLUX</th>
                      <th className="pb-2 text-right">Recibirá</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                    {cuotasData.cuotas.map((cuota) => {
                      const selected = isCuotaSelected(venta.id, cuota.id);
                      return (
                        <tr key={cuota.id} className={selected ? 'bg-primary/5' : ''}>
                          <td className="py-2 pl-6 pr-6">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleCuota(venta.id, cuota.id)}
                              className="h-4.5 w-4.5 cursor-pointer rounded border-slate-300 accent-flux-primary focus:ring-2 focus:ring-flux-primary dark:border-slate-600"
                            />
                          </td>
                          <td className="py-2">
                            Cuota {String(cuota.cuotaNum).padStart(2, '0')} de {cuota.totalCuotas}
                          </td>
                          <td className="py-2">{cuota.fechaAbono}</td>
                          <td className="py-2 font-medium">{fmt(cuota.montoOriginal)}</td>
                          <td className="py-2">{cuota.diasAdelanto} días</td>
                          <td className="py-2 text-slate-500">
                            <ComisionCell comision={cuota.comisionFlux} tye={null} size="xs" />
                          </td>
                          <td className="py-2 text-right font-bold text-slate-700 dark:text-slate-300">
                            {fmt(cuota.recibiras)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="py-4 text-sm text-slate-400">No hay cuotas disponibles.</p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

type ConnectionStatus = 'syncing' | 'error' | 'active' | 'loading';

export default function CuotasDisponibles() {
  const [ventas, setVentas] = useState<VentaListItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loadingVentas, setLoadingVentas] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [cuotasMap, setCuotasMap] = useState<Record<string, VentaCuotasResponse>>({});
  const [loadingCuotasIds, setLoadingCuotasIds] = useState<Set<string>>(new Set());
  const [acquirente, setAcquirente] = useState('');
  const [fechaDesde, setFechaDesde] = useState<Date | undefined>(undefined);
  const [fechaHasta, setFechaHasta] = useState<Date | undefined>(undefined);
  const [fechaDesdeOpen, setFechaDesdeOpen] = useState(false);
  const [fechaHastaOpen, setFechaHastaOpen] = useState(false);
  // Filtros aplicados (solo se actualizan al presionar "Filtrar")
  const [appliedFilters, setAppliedFilters] = useState<{ adquirente: string; fechaDesde?: string; fechaHasta?: string }>({ adquirente: '' });
  const [page, setPage] = useState(1);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('loading');
  const [acquirerOptions, setAcquirerOptions] = useState<{ slug: string; name: string }[]>([]);

  const { registerVenta, updateVentaCuotas, registerOnSolicitudConfirmada, toggleAllCuotas, isAllSelected, isIndeterminate, ventaRegistry, setMontoMinimo } = useAdelantoCart();
  const globalCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { httpClient } = await import('@/lib/http-client');
        const { getEndpointUrl } = await import('@/lib/api-config');
        const url = getEndpointUrl('acquirerConnections', 'list');
        const data = await httpClient.get<{ connections: { status: string }[] }>(url, { shouldCache: false });
        const conns = data.connections ?? [];
        if (conns.some((c) => c.status === 'syncing')) {
          setConnectionStatus('syncing');
        } else if (conns.some((c) => c.status === 'error')) {
          setConnectionStatus('error');
        } else {
          setConnectionStatus('active');
        }
      } catch {
        setConnectionStatus('active');
      }
      // Cargar adquirentes disponibles para el dropdown de filtro
      try {
        const { httpClient } = await import('@/lib/http-client');
        const { getEndpointUrl } = await import('@/lib/api-config');
        const url = getEndpointUrl('acquirerConnections', 'acquirers');
        const data = await httpClient.get<{ acquirers: { slug: string; name: string }[] }>(url);
        setAcquirerOptions(data.acquirers ?? []);
      } catch {
        // silenciar error, dropdown queda vacío
      }
    };
    load();
  }, []);

  const loadVentas = useCallback(async () => {
    setLoadingVentas(true);
    try {
      const res = await adelantosService.fetchVentas({
        adquirente: appliedFilters.adquirente,
        page,
        fechaDesde: appliedFilters.fechaDesde,
        fechaHasta: appliedFilters.fechaHasta,
      });
      setVentas(res.data);
      setPagination(res.pagination);
      if (res.montoMinimo !== undefined) setMontoMinimo(res.montoMinimo);
      res.data.forEach((v) => {
        registerVenta(v.id, {
          fechaVenta: v.fechaVenta,
          adquirente: v.adquirente,
          cuotas: [],
          comisionFlux: null,
          tye: null,
          recibiras: null,
        });
      });
    } finally {
      setLoadingVentas(false);
    }
  }, [appliedFilters, page, registerVenta]);

  useEffect(() => {
    loadVentas();
  }, [loadVentas]);

  useEffect(() => {
    const unregister = registerOnSolicitudConfirmada(() => {
      setExpandedIds(new Set());
      setCuotasMap({});
      setPage(1);
      loadVentas();
    });
    return unregister;
  }, [registerOnSolicitudConfirmada, loadVentas]);

  const loadCuotas = useCallback(
    async (ventaId: string): Promise<VentaCuotasResponse | null> => {
      if (cuotasMap[ventaId]) return cuotasMap[ventaId];
      setLoadingCuotasIds((prev) => new Set(prev).add(ventaId));
      try {
        const data = await adelantosService.fetchVentaCuotas(ventaId);
        setCuotasMap((prev) => ({ ...prev, [ventaId]: data }));
        updateVentaCuotas(ventaId, data);
        return data;
      } finally {
        setLoadingCuotasIds((prev) => {
          const next = new Set(prev);
          next.delete(ventaId);
          return next;
        });
      }
    },
    [cuotasMap, updateVentaCuotas],
  );

  const toggleExpand = useCallback(
    async (ventaId: string) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(ventaId)) {
          next.delete(ventaId);
        } else {
          next.add(ventaId);
        }
        return next;
      });

      await loadCuotas(ventaId);
    },
    [loadCuotas],
  );

  const selectAllVentas = useCallback(async () => {
    for (const venta of ventas) {
      const registeredVenta = ventaRegistry[venta.id];
      if (!registeredVenta || registeredVenta.cuotas.length === 0) {
        const freshData = await loadCuotas(venta.id);
        if (freshData) {
          toggleAllCuotas(venta.id, freshData.cuotas.map((c) => c.id));
        }
      } else {
        toggleAllCuotas(venta.id);
      }
    }
  }, [ventas, ventaRegistry, loadCuotas, toggleAllCuotas]);

  const allGlobalSelected = ventas.length > 0 && ventas.every((v) => isAllSelected(v.id));
  const someSelected = ventas.some((v) => isAllSelected(v.id) || isIndeterminate(v.id));
  const globalIndeterminate = someSelected && !allGlobalSelected;

  useEffect(() => {
    if (globalCheckboxRef.current) globalCheckboxRef.current.indeterminate = globalIndeterminate;
  }, [globalIndeterminate]);

  const handleFilter = () => {
    setPage(1);
    setExpandedIds(new Set());
    setCuotasMap({});
    setAppliedFilters({
      adquirente: acquirente,
      fechaDesde: fechaDesde ? format(fechaDesde, 'yyyy-MM-dd') : undefined,
      fechaHasta: fechaHasta ? format(fechaHasta, 'yyyy-MM-dd') : undefined,
    });
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="p-6">
      <div className="flex flex-wrap items-end gap-4 mb-8 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
        <div className="w-full lg:w-48">
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">
            Adquirente
          </label>
          <select
            value={acquirente}
            onChange={(e) => setAcquirente(e.target.value)}
            className="w-full h-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary px-3"
          >
            <option value="">Todos los Adquirentes</option>
            {acquirerOptions.map((a) => (
              <option key={a.slug} value={a.slug}>{a.name}</option>
            ))}
          </select>
        </div>
        <div className="w-full lg:w-48">
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Desde</label>
          <Popover open={fechaDesdeOpen} onOpenChange={setFechaDesdeOpen}>
            <PopoverTrigger asChild>
              <button className="w-full h-10 flex items-center gap-2 pl-3 pr-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-left hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
                <span className="material-symbols-outlined text-slate-400 text-[18px] flex-shrink-0">calendar_month</span>
                <span className={`flex-1 truncate ${fechaDesde ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
                  {fechaDesde ? format(fechaDesde, 'dd/MM/yyyy') : 'Fecha desde'}
                </span>
                {fechaDesde && (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); setFechaDesde(undefined); }}
                    className="material-symbols-outlined text-slate-400 hover:text-slate-600 text-[18px] flex-shrink-0 cursor-pointer"
                  >
                    close
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={fechaDesde} onSelect={(date) => { setFechaDesde(date); setFechaDesdeOpen(false); }} locale={es} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div className="w-full lg:w-48">
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Hasta</label>
          <Popover open={fechaHastaOpen} onOpenChange={setFechaHastaOpen}>
            <PopoverTrigger asChild>
              <button className="w-full h-10 flex items-center gap-2 pl-3 pr-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-left hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
                <span className="material-symbols-outlined text-slate-400 text-[18px] flex-shrink-0">calendar_month</span>
                <span className={`flex-1 truncate ${fechaHasta ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
                  {fechaHasta ? format(fechaHasta, 'dd/MM/yyyy') : 'Fecha hasta'}
                </span>
                {fechaHasta && (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); setFechaHasta(undefined); }}
                    className="material-symbols-outlined text-slate-400 hover:text-slate-600 text-[18px] flex-shrink-0 cursor-pointer"
                  >
                    close
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={fechaHasta} onSelect={(date) => { setFechaHasta(date); setFechaHastaOpen(false); }} locale={es} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <button
          onClick={handleFilter}
          className="bg-primary text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2 h-10"
        >
          <span className="material-symbols-outlined text-sm">filter_alt</span> Filtrar
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="text-slate-400 text-xs font-semibold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
              <th className="px-4 py-3 w-10">
                <input
                  ref={globalCheckboxRef}
                  type="checkbox"
                  checked={allGlobalSelected}
                  onChange={selectAllVentas}
                  className="h-4.5 w-4.5 cursor-pointer rounded border-slate-300 accent-flux-primary focus:ring-2 focus:ring-flux-primary dark:border-slate-600"
                />
              </th>
              <th className="px-4 py-3 w-8" />
              <th className="px-4 py-3">Fecha Venta</th>
              <th className="px-4 py-3">Adquirente</th>
              <th className="px-4 py-3">Cód. Comercio</th>
              <th className="px-4 py-3">Cód. Autorización</th>
              <th className="px-4 py-3">Cuotas</th>
              <th className="px-4 py-3">Abono Original</th>
              <th className="px-4 py-3">Comisión FLUX</th>
              <th className="px-4 py-3">Recibirás</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loadingVentas ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-4 py-4">
                    <div className="h-4 w-4 bg-slate-200 dark:bg-slate-700 rounded" />
                  </td>
                  <td className="px-4 py-4">
                    <div className="h-4 w-4 bg-slate-200 dark:bg-slate-700 rounded-full" />
                  </td>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-4 py-4">
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : ventas.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-16 text-center">
                  {connectionStatus === 'syncing' ? (
                    <div className="flex flex-col items-center gap-3">
                      <span className="material-symbols-outlined text-4xl text-amber-500 animate-spin">sync</span>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Se estan sincronizando tus cuotas con el adquiriente
                      </p>
                      <p className="text-xs text-slate-500">Este proceso puede tomar unos minutos</p>
                    </div>
                  ) : connectionStatus === 'error' ? (
                    <div className="flex flex-col items-center gap-3">
                      <span className="material-symbols-outlined text-4xl text-red-500">error</span>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Hubo un error al sincronizar con el adquiriente
                      </p>
                      <p className="text-xs text-slate-500">
                        Revisa tus credenciales en{' '}
                        <a
                          href="/configuraciones/credenciales-adquirente"
                          className="text-primary underline hover:no-underline"
                        >
                          configuracion
                        </a>
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <span className="material-symbols-outlined text-4xl text-slate-400">inbox</span>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        No hay cuotas disponibles
                      </p>
                      <p className="text-xs text-slate-500">
                        No se encontraron cuotas disponibles para adelantar
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              ventas.map((venta) => (
                <VentaRow
                  key={venta.id}
                  venta={venta}
                  expanded={expandedIds.has(venta.id)}
                  onToggleExpand={() => toggleExpand(venta.id)}
                  onLoadCuotas={loadCuotas}
                  cuotasData={cuotasMap[venta.id] ?? null}
                  loadingCuotas={loadingCuotasIds.has(venta.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {(!loadingVentas && ventas.length === 0) ? null : (
        <div className="mt-8 flex justify-between items-center border-t border-slate-100 dark:border-slate-800 pt-6">
          <div className="text-sm text-slate-500">
            Mostrando página {pagination.page} de {pagination.totalPages} ({pagination.total} {pagination.total === 1 ? 'venta' : 'ventas'})
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 mr-4">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-400 hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Pág. {pagination.page} de {pagination.totalPages}
              </span>
              <button
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
            <button
              onClick={selectAllVentas}
              className="px-8 py-2 bg-primary text-white rounded-lg text-sm font-bold shadow-md hover:bg-blue-700 transition-colors"
            >
              Solicitar Todo el Disponible
            </button>
          </div>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
